import { TFunction } from 'i18next'

import { CONFIG_ENABLE_EDD } from '@src/config/env'
import keyBy from '@src/quince-site-common/src/utils/keyBy'

import { EDDDetails, EDDProductType, VariantDetail } from '../../../services/eddService/eddService.types'

export const getEDDDetails = (
  variantEDDDetails: number[] | undefined,
  eddDetails: EDDProductType | undefined,
  zipCode?: string | null,
): EDDDetails => {
  if (!CONFIG_ENABLE_EDD || !zipCode) {
    return {
      showEDD: false,
      isExpressShipping: false,
    }
  }

  let standardDeliveryEDD: EDDDetails | null = null
  let standardPremiumDeliveryEDD: EDDDetails | null = null

  variantEDDDetails?.forEach(variantEDDDetail => {
    const eddDetail: VariantDetail | undefined = eddDetails?.[variantEDDDetail]
    const inventoryLocations = eddDetail?.location_ids

    if (eddDetail) {
      const eddDetailMap = keyBy(eddDetail.delivery_types, `type`)
      const delivery = eddDetailMap.LB_STANDARD
      if (delivery?.is_faster_edd) {
        standardDeliveryEDD = {
          ...delivery,
          inventoryLocations,
          showEDD: delivery.is_faster_edd,
          isExpressShipping: false,
          cutoffDate: delivery.cutoff_epoch,
          cutoffTTL: delivery.cutoff_ttl_sec,
        }
      }
      const standardPremiumDelivery = eddDetailMap.LB_STANDARD_PREMIUM
      if (standardPremiumDelivery?.is_faster_edd) {
        standardPremiumDeliveryEDD = {
          ...standardPremiumDelivery,
          inventoryLocations,
          showEDD: standardPremiumDelivery.is_faster_edd,
          isExpressShipping: false,
          cutoffDate: standardPremiumDelivery.cutoff_epoch,
          cutoffTTL: standardPremiumDelivery.cutoff_ttl_sec,
        }
      }
    }
  })

  if (standardPremiumDeliveryEDD) return standardPremiumDeliveryEDD

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (standardDeliveryEDD) return standardDeliveryEDD

  return {
    showEDD: false,
    isExpressShipping: false,
  }
}

export const useEDDDetailFromSelectedOptions = (
  eddDetails: EDDProductType | undefined,
  selectedOptionsVariantIds: number[] | undefined,
  zipCode?: string,
) => {
  return getEDDDetails(selectedOptionsVariantIds, eddDetails, zipCode)
}

export const generateEDDLabel = (eddDetails: EDDDetails | undefined, t: TFunction) => {
  if (!eddDetails?.max_date || !eddDetails.showEDD) return

  let dateInDateStringFormat = eddDetails.max_date

  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

  if (dateRegex.test(dateInDateStringFormat)) {
    dateInDateStringFormat += `T00:00:00`
  }

  const date = new Date(dateInDateStringFormat)

  const day = date.getDay()
  const dayOfMonth = date.getDate()

  // Month names for formatted display
  const MONTH_NAMES = [
    t('jan'),
    t('feb'),
    t('mar'),
    t('apr'),
    t('may'),
    t('jun'),
    t('jul'),
    t('aug'),
    t('sep'),
    t('oct'),
    t('nov'),
    t('dec'),
  ]

  const DAY_NAMES = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')]

  let label = t('free_delivery_by_var1_var2_var3', {
    var1: DAY_NAMES[day],
    var2: MONTH_NAMES[date.getMonth()],
    var3: dayOfMonth,
  })

  if (eddDetails.type === 'LB_STANDARD_PREMIUM') {
    label = t('estimated_delivery_with_days', {
      day: DAY_NAMES[day],
      month: MONTH_NAMES[date.getMonth()],
      dayOfMonth: dayOfMonth,
      days: eddDetails.max_cal_days,
    })
  } else if (eddDetails.max_cal_days === 0) {
    label = t('free_delivery_today_template', { month: MONTH_NAMES[date.getMonth()], dayOfMonth: dayOfMonth })
  } else if (eddDetails.max_cal_days === 1) {
    label = t('free_delivery_tomorrow_template', { var1: MONTH_NAMES[date.getMonth()], dayOfMonth: dayOfMonth })
  } else if (eddDetails.max_cal_days === 2) {
    label = t('free_2_day_delivery_by_va_l8g8oi', {
      var1: DAY_NAMES[day],
      var2: MONTH_NAMES[date.getMonth()],
      dayOfMonth: dayOfMonth,
    })
  }

  return label
}

export const getCutoffTime = (cutoffDateInEpochSeconds: number, t: TFunction) => {
  const currentDateInEpochSeconds = Math.floor(Date.now() / 1000)
  const totalSeconds = cutoffDateInEpochSeconds - currentDateInEpochSeconds

  if (totalSeconds <= 0) {
    return null
  }

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  let label = t('order_within')
  if (hours > 0) {
    label += `${hours} ${hours === 1 ? t('hour') : t('hours')}`
    if (minutes > 0) {
      label += ` ${minutes} ${minutes === 1 ? t('min') : t('mins')}`
    }
  } else if (minutes > 0) {
    label += `${minutes} ${minutes === 1 ? t('min') : t('mins')}`
  } else {
    label += t('order_within_1_minute')
  }

  return label.trim()
}
